import { create } from "zustand"
import { persist } from "zustand/middleware"
import type { AuthResponse } from "@/lib/api/http"
import { useNotificationStore } from "./notification.store"

// ── Types ───────────────────────────────────────────────────────────────────────
interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  user: AuthResponse["user"] | null
  isAuthenticated: boolean

  // Seller-specific — extracted from employeeProfile for convenient access
  sellerProfileId: string | null   // employeeProfile.id
  sellerCompanyId: string | null   // employeeProfile.companyId (= company.id)

  // Buyer-specific — extracted from buyer for convenient access
  buyerId: string | null           // buyer.id

  // Actions
  setAuth: (response: AuthResponse) => void
  setSellerCompanyId: (id: string) => void
  setSellerProfileId: (id: string) => void
  clearAuth: () => void
}

// ── Store ───────────────────────────────────────────────────────────────────────
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      sellerProfileId: null,
      sellerCompanyId: null,
      buyerId: null,

      setAuth: (response) =>
        set({
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          user: response.user,
          isAuthenticated: true,
          // Extract seller profile & company IDs directly from the login response
          sellerProfileId: response.user.employeeProfile?.id ?? null,
          sellerCompanyId: response.user.employeeProfile?.companyId ?? null,
          buyerId: response.user.buyer?.id ?? null,
        }),

      setSellerCompanyId: (id: string) =>
        set((state) => ({
          sellerCompanyId: id,
          user: state.user
            ? {
                ...state.user,
                employeeProfile: state.user.employeeProfile
                  ? { ...state.user.employeeProfile, companyId: id }
                  : {
                      id: state.sellerProfileId ?? "",
                      userId: state.user.id,
                      companyId: id,
                      designation: "",
                      department: "",
                      phoneNumber: "",
                      bio: "",
                      verificationStatus: "PENDING",
                    },
              }
            : null,
        })),

      setSellerProfileId: (id: string) =>
        set((state) => ({
          sellerProfileId: id,
          user: state.user
            ? {
                ...state.user,
                employeeProfile: state.user.employeeProfile
                  ? { ...state.user.employeeProfile, id }
                  : {
                      id,
                      userId: state.user.id,
                      companyId: state.sellerCompanyId,
                      designation: "",
                      department: "",
                      phoneNumber: "",
                      bio: "",
                      verificationStatus: "PENDING",
                    },
              }
            : null,
        })),

      clearAuth: () => {
        localStorage.clear()
        useNotificationStore.getState().clearNotifications()
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
          sellerProfileId: null,
          sellerCompanyId: null,
          buyerId: null,
        })
      },
    }),
    {
      name: "auth", // localStorage key
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        sellerProfileId: state.sellerProfileId,
        sellerCompanyId: state.sellerCompanyId,
        buyerId: state.buyerId,
      }),
    }
  )
)
